import { createLayerTemplate, updateLayerTemplateProperties } from '@/api/layerTemplates';
import { getLayer, getLayerAttributes } from '@/api/layers';
import { LayerPropertiesByCategory } from '@/components/Layers/LayerPropertiesByCategory';
import { FieldLayerPrefillSelects } from '@/components/Templates/FieldLayerPrefillSelects';
import { LayerTemplateCreate } from '@/types/layerTemplate';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

interface LayerTemplateCreateModalProps {
  open: boolean;
  onClose: () => void;
  onTemplateCreated?: () => void;
}

// Visibility status UUIDs
const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

export const LayerTemplateCreateModal: React.FC<LayerTemplateCreateModalProps> = ({
  open,
  onClose,
  onTemplateCreated,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<LayerTemplateCreate>({
    name: '',
    description: '',
    type: '',
    is_public: false,
    visibility_status_id: PRIVATE_UUID,  // Default to private
  });

  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';
  const [selectedLayerId, setSelectedLayerId] = useState<string>('');
  const [propertiesData, setPropertiesData] = useState<{ [key: string]: string | number }>({});
  const [allAttributes, setAllAttributes] = useState<Array<{id: string; name: string; unit?: string | null; type: string; description: string | null}>>([]);

  useEffect(() => {
    if (open) {
      loadAllAttributes();
      // Reset form when opening
      setFormData({
        name: '',
        description: '',
        type: '',
        is_public: false,
        visibility_status_id: PRIVATE_UUID,
      });
      setPropertiesData({});
      setSelectedLayerId('');
      setError(null);
    }
  }, [open]);

  const loadAllAttributes = async () => {
    try {
      const attributes = await getLayerAttributes();
      setAllAttributes(attributes);

      // Initialize empty properties data for all attributes
      const initialProperties: { [key: string]: string | number } = {};
      attributes.forEach(attr => {
        initialProperties[attr.name] = '';
      });
      setPropertiesData(initialProperties);
    } catch (err) {
      console.error('Error loading layer attributes:', err);
    }
  };



  const handleLayerSelect = async (layerId: string) => {
    setSelectedLayerId(layerId);

    if (!layerId) {
      // Reset to empty values for all attributes
      const resetProperties: { [key: string]: string | number } = {};
      allAttributes.forEach(attr => {
        resetProperties[attr.name] = '';
      });
      setPropertiesData(resetProperties);
      return;
    }

    try {
      // Get details of selected layer
      const layerDetail = await getLayer(layerId);

      // Start with empty values for all attributes
      const newPropertiesData: { [key: string]: string | number } = {};
      allAttributes.forEach(attr => {
        newPropertiesData[attr.name] = '';
      });

      // Fill with values from layer if they exist
      if (layerDetail.properties) {
        layerDetail.properties.forEach(layerProp => {
          if (layerProp.value !== null && layerProp.value !== undefined && layerProp.value !== '') {
            newPropertiesData[layerProp.name] = layerProp.value;
          }
        });
      }

      setPropertiesData(newPropertiesData);
    } catch (err) {
      console.error('Error loading layer details:', err);
      setError('Failed to load layer details');
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create template first
      const newTemplate = await createLayerTemplate(formData);

      // Update template properties with the filled data
      const propertiesUpdate = Object.entries(propertiesData)
        .filter(([name, value]) => value !== null && value !== undefined && value !== '')
        .map(([name, value]) => {
          const attribute = allAttributes.find(attr => attr.name === name);
          if (!attribute || !attribute.id) {
            return null;
          }
          return {
            attribute_id: attribute.id,
            value: value,
          };
        })
        .filter((p): p is { attribute_id: string; value: string | number } => p !== null && !!p.attribute_id);

      console.log('Properties update payload:', propertiesUpdate);

      // Update template properties if we have any
      if (propertiesUpdate.length > 0) {
        await updateLayerTemplateProperties(newTemplate.id, propertiesUpdate);
      }

      onTemplateCreated?.();
      handleClose();
    } catch (err) {
      console.error('Error creating template:', err);
      setError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      type: '',
      is_public: false,
      visibility_status_id: PRIVATE_UUID,
    });
    setPropertiesData({});
    setSelectedLayerId('');
    setError(null);
    onClose();
  };

  const handlePropertyChange = (propertyName: string, value: string | number) => {
    setPropertiesData(prev => ({
      ...prev,
      [propertyName]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create New Reservoir Template
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" mb={2}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <Box>
            {/* Template Basic Info */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="oil">Oil</MenuItem>
                    <MenuItem value="gas">Gas</MenuItem>
                    <MenuItem value="oil_and_gas">Oil & Gas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={formData.visibility_status_id || PRIVATE_UUID}
                    label="Visibility"
                    onChange={(e) => {
                      const selectedId = e.target.value as string;
                      setFormData(prev => ({
                        ...prev,
                        visibility_status_id: selectedId,
                        is_public: selectedId === PUBLIC_STATUS_UUID  // Keep for backward compatibility
                      }));
                    }}
                  >
                    <MenuItem value={PRIVATE_UUID}>Private</MenuItem>
                    <MenuItem value={COMPANY_PUBLIC_UUID}>Company Public</MenuItem>
                    {isSuperAdmin && (
                      <MenuItem value={PUBLIC_STATUS_UUID}>Public</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Describe when and how to use this template"
                  multiline
                  minRows={3}
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
            </Grid>

            {/* Layer Selection for Pre-filling */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Pre-fill from Existing Reservoir (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a field, then a layer to copy property values into this template.
              </Typography>
              <FieldLayerPrefillSelects
                selectedLayerId={selectedLayerId}
                onLayerSelect={handleLayerSelect}
                disabled={loading}
              />
            </Box>

            {/* All Available Properties */}
            {allAttributes.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Template Properties
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedLayerId
                    ? 'Values filled from selected layer. You can modify any property below.'
                    : 'Fill in the properties for your template manually.'
                  }
                </Typography>
                <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                  <LayerPropertiesByCategory
                    properties={allAttributes.map(attr => ({
                      name: attr.name,
                      unit: attr.unit,
                      value_type: attr.type,
                      description: attr.description || null,
                    }))}
                    propertiesData={propertiesData}
                    onPropertyChange={handlePropertyChange}
                    disabled={false}
                    readOnly={false}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Note: Empty properties will be created with default values and can be edited after template creation.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading || !formData.name.trim()}
        >
          {loading ? 'Creating...' : 'Create Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
